import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef } from "react"
import { Platform, TouchableOpacity, View } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { MAX_CONTENT_WIDTH } from "../config"
import {
  BottomSheetBackdrop,
  BottomSheetHandle,
  BottomSheetModal,
  BottomSheetScrollView
} from "@gorhom/bottom-sheet"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import Haptics from "./haptics"
import { usePopover, showPopoverFromRef } from "./popover"
import { Ionicons } from '@expo/vector-icons'
import { log } from "./pwa"
import Analytics from "./analytics"

type ActionSheetName =
  | 'add-company'
  | 'manage-accounts'
  | 'company-data-feedback'
  | 'specify-values'
  | 'score-explanation'
  | 'company-website'
  | 'certification-website'
  | 'feedback-form'
  | 'permissions-settings'
  | 'transactions-add-company'
  | 'transactions-search'
  | 'settings-specify-values'
  | 'settings-feedback-form'
  | 'onboarding-specify-values'
  | 'onboarding-add-accounts'

type CustomActionSheetProps = Omit<React.ComponentProps<typeof BottomSheetModal>, 'children'> & {
  children?: React.ReactNode
  noInput?: boolean
  name: ActionSheetName
  menuContent?: React.ReactNode
}

type CustomActionSheetScrollViewProps =
  React.ComponentProps<typeof BottomSheetScrollView>
  & { id?: string }

export type CustomActionSheetRef = {
  hide: () => void;
  show: () => void;
}

const CustomActionSheet = forwardRef<CustomActionSheetRef, CustomActionSheetProps>(
  ({ children, noInput, menuContent, name, ...props}: CustomActionSheetProps,
    ref) => {
    const theme = useContext(ThemeContext)
    const { bgMain, borderColor, textMain } = theme.theme.colors

    const { bottom } = useSafeAreaInsets()
    const { showFromAnchor, hide } = usePopover()
    const ellipsisRef = useRef<React.ElementRef<typeof TouchableOpacity>>(null)
    
    // best practice in docs to avoid re-rendering
    const snapPoints = useMemo(() => {
      return ['90%']
    }, [])

    const internalModalRef = useRef<BottomSheetModal>(null)
    const openedAtRef = useRef<number | null>(null)

    const trackClose = useCallback(() => {
      try {
        const now = Date.now()
        const openedAt = openedAtRef.current
        const durationMs = openedAt ? now - openedAt : undefined

        if (!name) {
          log.warn('no name provided to track action sheet view')
        }

        Analytics.track('action_sheet_view', {
          name: name || 'unknown',
          durationMs,
          durationSeconds: durationMs != null ? Math.round(durationMs / 1000) : undefined,
        })
      } catch (_e) {
        // no-op
      }
      openedAtRef.current = null
    }, [name])

    useImperativeHandle(ref, () => ({
      show: () => {
        Haptics.impactLight()
        openedAtRef.current = Date.now()
        internalModalRef.current?.present()
      },
      hide: () => {
        Haptics.impactLight()
        internalModalRef.current?.dismiss()
      }
    }))

    useEffect(() => {
      // safety: if component unmounts while open, record close
      return () => {
        if (openedAtRef.current) {
          trackClose()
        }
      }
    }, [trackClose])

    return (
      <BottomSheetModal
        ref={internalModalRef}
        // default props
        snapPoints={noInput ? undefined : snapPoints}
        enableDynamicSizing={noInput ? true : false}
        enableHandlePanningGesture={true}
        keyboardBehavior='interactive'
        keyboardBlurBehavior='none'
        backdropComponent={(props) => {
          return (
            <BottomSheetBackdrop
              appearsOnIndex={0}
              disappearsOnIndex={-1}
              onPress={() => {
                Haptics.impactLight()
                trackClose()
                internalModalRef.current?.dismiss()
              }}
              {...props}
            />
          )
        }}
        backgroundStyle={{
          backgroundColor: bgMain,
        }}
        onDismiss={() => {
          hide()
        }}
        handleComponent={(props) => {
          return <>
            <BottomSheetHandle
              {...props}
              indicatorStyle={{
                backgroundColor: borderColor,
              }}
            />
            <View style={{ position: "absolute", top: 10, right: 10}}>
              <TouchableOpacity
                onPress={() => {
                  Haptics.impactLight()
                  trackClose()
                  internalModalRef.current?.dismiss()
                }}
                hitSlop={10}
              >
                <Ionicons name="close" size={24} color={textMain} />
              </TouchableOpacity>
            </View>
            {
              menuContent ? (
                <View style={{ position: "absolute", top: 10, left: 10}}>
                  <TouchableOpacity
                    ref={ellipsisRef}
                    onPress={() => {
                      Haptics.impactLight()
                      showPopoverFromRef(ellipsisRef, showFromAnchor, (
                        menuContent
                      ), undefined, {
                        offset: { x: 0, y: 0 },
                      })
                    }}
                    hitSlop={10}
                  >
                    <Ionicons name="ellipsis-horizontal" size={24} color={textMain} />
                  </TouchableOpacity>
                </View>
              ) : null
            }
          </>
        }}
        {...props}
        onChange={(index) => {
          // BottomSheetModal onChange gives index of current snap; -1 means closed
          if (index === -1) {
            trackClose()
          }
          // invoke consumer onChange if provided
          const consumerOnChange = (props as { onChange?: (index: number) => void }).onChange
          consumerOnChange?.(index)
        }}
      >
        {
          // dynamic sizing has odd behabior without bottomsheetscrollview
          // bounces={false} is required to avoid scrolling unscrollable content
          noInput ? (
            <BottomSheetScrollView
              bounces={false}
              style={{
                flex: 1,
                height: Platform.OS === "web" ? "85%" : "100%",
                paddingBottom: bottom,
                width: "100%",
                maxWidth: MAX_CONTENT_WIDTH,
                alignSelf: "center",
              }}
              contentContainerStyle={{
                paddingTop: 16,
                paddingBottom: bottom,
              }}
            >
              {children}
            </BottomSheetScrollView>
          ) : (
            <View
              style={{
                flex: 1,
                height: Platform.OS === "web" ? "85%" : "100%",
                paddingBottom: bottom || 16,
                paddingTop: 16,
                width: "100%",
                maxWidth: MAX_CONTENT_WIDTH,
                alignSelf: "center"
              }}
            >
              {children}
            </View>
          )
        }
      </BottomSheetModal>
    )
  }
)

CustomActionSheet.displayName = "CustomActionSheet"

const CustomActionSheetScrollView:React.FC<CustomActionSheetScrollViewProps> = (
  props: CustomActionSheetScrollViewProps
) => {
  // id messes with inner scroll mechanism, so we ignore it
  const { id: ignoredId, ...scrollProps } = props
  void ignoredId

  return (
    <BottomSheetScrollView
      contentContainerStyle={{
        gap: 16,
        paddingHorizontal: 16
      }}
      {...scrollProps}
    >
      {props.children}
    </BottomSheetScrollView>
  )
}

export {
  CustomActionSheet,
  CustomActionSheetScrollView
}
